import { useState } from 'react';
import { Link } from 'react-router-dom';
import { App, Button, Modal, Popconfirm, Select, Space, Tag, Typography } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { usePermissions } from '../../auth/store';
import { adminLinks } from '../../lib/adminLinks';
import { errMsg } from '../../lib/errors';
import type { AgencyListRow } from '../agencies/agencyApi';

interface UserAgency {
  agency: { id: string; code: string; name: string; source: 'manual' | 'link' | 'admin'; attributedAt: string } | null;
}

const SOURCE: Record<string, string> = { manual: 'typed code', link: 'invite link', admin: 'assigned by admin' };

/**
 * The agency a user belongs to, on the user drawer. Assigning or changing it
 * only affects recharges made AFTER the change — earlier commission stays with
 * whoever earned it.
 */
export function UserAgencyControl({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { can } = usePermissions();
  const [open, setOpen] = useState(false);
  const [agencyId, setAgencyId] = useState<string | undefined>();
  const key = ['admin', 'users', userId, 'agency'];
  const query = useQuery({
    queryKey: key,
    enabled: can('agencies.view'),
    queryFn: async () => (await api.get<UserAgency>(`/admin/users/${userId}/agency`)).data,
  });
  const agencies = useQuery({
    queryKey: ['admin', 'agencies', 'active', ''],
    enabled: open,
    queryFn: async () => (await api.get<AgencyListRow[]>('/admin/agencies', { params: { status: 'active' } })).data,
  });
  const done = (msg: string) => {
    message.success(msg);
    setOpen(false);
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: ['admin', 'agencies'] });
  };
  const assign = useMutation({
    mutationFn: (id: string) => api.put(`/admin/users/${userId}/agency`, { agencyId: id }),
    onSuccess: () => done('Agency set — applies to recharges from now on'),
    onError: (e) => message.error(errMsg(e, 'Could not set the agency')),
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/admin/users/${userId}/agency`),
    onSuccess: () => done('Agency removed'),
    onError: (e) => message.error(errMsg(e, 'Could not remove the agency')),
  });

  if (!can('agencies.view')) return <>—</>;
  const a = query.data?.agency;
  return (
    <Space wrap>
      {a ? (
        <>
          <Link to={adminLinks.agency(a.id)}>{a.name}</Link>
          <Tag>{a.code}</Tag>
          <Typography.Text type="secondary">
            {SOURCE[a.source]} · {new Date(a.attributedAt).toLocaleDateString()}
          </Typography.Text>
        </>
      ) : (
        <Typography.Text type="secondary">{query.isLoading ? '…' : 'None'}</Typography.Text>
      )}
      <Can permission="agencies.assign_user">
        <Button size="small" onClick={() => setOpen(true)}>
          {a ? 'Change' : 'Assign'}
        </Button>
        {a && (
          <Popconfirm title="Remove this user from the agency? Future recharges earn it nothing." onConfirm={() => remove.mutate()}>
            <Button size="small" danger loading={remove.isPending}>
              Remove
            </Button>
          </Popconfirm>
        )}
      </Can>
      <Modal
        open={open}
        title="Assign agency"
        okText="Assign"
        okButtonProps={{ disabled: !agencyId }}
        confirmLoading={assign.isPending}
        onCancel={() => setOpen(false)}
        onOk={() => agencyId && assign.mutate(agencyId)}
      >
        <Typography.Paragraph type="secondary">
          Commission for this user&apos;s recharges goes to the chosen agency from now on. Earlier recharges are not moved.
        </Typography.Paragraph>
        <Select
          showSearch
          style={{ width: '100%' }}
          placeholder="Choose an agency"
          loading={agencies.isLoading}
          value={agencyId}
          onChange={setAgencyId}
          optionFilterProp="label"
          options={(agencies.data ?? []).map((g) => ({ value: g.id, label: `${g.name} (${g.code})` }))}
        />
      </Modal>
    </Space>
  );
}
